/*
* Portal Users: Reserve Study scenario form
*/
import './report.css';

import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Button, ButtonToolbar, Modal, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as MutateActions from '../actions/mutate';
import Auth from '../auth/auth';
import * as AppComp from '../common/app-component-base';
import * as AppUtilities from '../common/apputilities';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import { GridUtilities } from '../common/grid/gridutilities';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import AppConfiguration from '../config/configuration';
import { PortalAccessActions, PortalRoles } from '../invite/create-invite';
import * as ReduxQueries from '../queries';
import * as ReportCommon from '../report/report-common';
import { UserUtilities } from '../user/user-utilities';
import * as ReportChartForm from './chartform';
import * as ReportCharts from './charts';
import * as ReportInventoryGrids from './inventorygrids';
import * as ReportLogic from './logic';
import * as PercentFundedDisplay from './percentfundedsection';

export interface IAssociationReportProps extends AppComp.IAppComponentProps {
    association?: any;
    associationId?: any;
    currentOrder?: any;
    navigateTo?: any;
    currentOrderChart?: any;
    inventory?: any;
    scenarios?: any;
    portalInvites?: any;
    toggleClientInvite?: any;
}

export interface IAssociationReportState extends AppComp.IAppComponentState {
    formChanges?: any;
    inventoryChanges?: any;
    showModalToggleInvite: boolean;
    toggleInvite?: any;
}

class AssociationReport extends AppComp.AppComponent<IAssociationReportProps, IAssociationReportState> {
    public originalExtendedInventoryCache: Array<any> = [];

    public constructor(props: IAssociationReportProps) {
        super(props, 'app.report-association.');

        this.state = {
            formChanges: {},
            inventoryChanges: [],
            showModalToggleInvite: false,
            toggleInvite: {}
        };

        ReportCharts.setHighchartOptions();
    }

    public extendAssociationData(record: any) {
        if (!record) { return; }

        let result = _.cloneDeep(record);
        const invSubgroups = AppUtilities.getArray(record, 'inventorySubgroups');
        if (AppUtilities.isArrayWithLength(invSubgroups)) {
            // result.inventorySubgroups.push({ groupCode: '', title: '', label: this.appText('filter-option-any') });
            result.inventorySubgroups = result.inventorySubgroups.map(c => {
                if (!c.label) {
                    c.label = Formatters.composeInventorySubgroupLabel(c, true); // c.label = `${c.groupCode} - ${c.title}`;
                }

                return c;
            });
        }

        return result;
    }

    public extendInventoryData(inventory: Array<any>, inventoryChanges: Array<any>, association: any) {
        // Extend grid row data
        if (!AppUtilities.isArrayWithLength(inventory)) { return []; }

        const extended = inventory.map((r, i) => {
            let record = _.cloneDeep(r);

            record.avgCost = ReportLogic.calculateInventoryItemAvgCost(record);
            record.avgCost_sort = Number(record.avgCost);
            if (!record.avgCost) {
                GridUtilities.recordSetRowClass(record, 'row-no-avg-cost');
                record.usefulLife = 'N/A';
                record.remainingUsefulLife = 0;
            }

            const invSubgroups = AppUtilities.getArray(association, 'inventorySubgroups');
            record.Group = '';
            if (AppUtilities.isArrayWithLength(invSubgroups)) {
                let subgroupDef: any = _.find(invSubgroups, ['groupCode', _.get(record, 'subgroup', '')]);
                if (subgroupDef && subgroupDef.title) { record.Group = subgroupDef.title; }
            }

            if (!AppUtilities.isNumeric(record.remainingUsefulLife)) {
                record.remainingUsefulLife = 0;
            }
            record.usefulLife_sort = (AppUtilities.isNumeric(record.usefulLife)) ? Formatters.int(record.usefulLife) : 0;
            record.remainingUsefulLife_sort = (AppUtilities.isNumeric(record.remainingUsefulLife)) ? Formatters.int(record.remainingUsefulLife) : 0;

            record.included = _.get(record, 'included', (record.avgCost_sort !== 0 && record.usefulLife !== 'N/A') ? true : false) as boolean;
            record.hypAvgCost = Formatters.float(_.get(record, 'hypAvgCost', record.avgCost_sort), 2);
            record.hypUsefulLife = Formatters.int(_.get(record, 'hypUsefulLife', record.usefulLife_sort));
            record.hypRemainingUsefulLife = Formatters.int(_.get(record, 'hypRemainingUsefulLife', record.remainingUsefulLife_sort));

            let invChanges = _.find(inventoryChanges, { inventoryId: record.inventoryId });
            if (invChanges) {
                record = _.merge(record, _.omit(invChanges, 'inventoryId'));
            }

            record.editing = record.included;

            return record;
        });

        if (this.originalExtendedInventoryCache.length === 0) { this.originalExtendedInventoryCache = _.cloneDeep(extended); }

        return extended;
    }

    public getFormData(currentOrderChart: any, currentOrder: any) {
        let result = {
            annualIncrease: Formatters.getFloat(currentOrderChart, 'recommendedAnnualIncrease', 3, 100, 0), // changed from reccomended
            // recommendedAnnualIncrease: Formatters.getFloat(currentOrderChart, 'recommendedAnnualIncrease', 3, 100, 0), // changed from reccomended
            // currentAnnualIncrease: Formatters.getFloat(currentOrderChart, 'currentAnnualIncrease', 100, 0),
            beginningBalance: Formatters.getFloat(currentOrderChart, 'begBalance', 2, 0),
            beginYear: Formatters.getInt(currentOrderChart, 'beginYear', new Date().getFullYear()),
            calculateYears: Formatters.getInt(currentOrderChart, 'years', 30),
            inflation: Formatters.getFloat(currentOrderChart, 'inflation', 3, 100, 0),
            currentYears: Formatters.getInt(currentOrderChart, 'currentYears', ReportCommon.CalculateYears),
            recommendedYears: Formatters.getInt(currentOrderChart, 'recommendedYears', ReportCommon.CalculateYears),
            minimumYears: Formatters.getInt(currentOrderChart, 'minimumYears', ReportCommon.CalculateYears),
            interest: Formatters.getFloat(currentOrderChart, 'interest', 3, 100, 0),
            mrcValue: Formatters.getFloat(currentOrderChart, 'recommendedMRCVal', Formatters.getFloat(currentOrder, 'recommendedMRC')),
            secondaryAnnualIncrease: Formatters.getFloat(currentOrderChart, 'recommendedSecondaryAnnualIncrease', 3, 100, 0),
            specialAssessment: Formatters.getFloat(currentOrderChart, 'sa', 2, 1, 0),
            years: Formatters.getInt(currentOrderChart, 'recommendedYears', 0),
        };

        return _.merge(result, this.state.formChanges);
    }

    // HANDLERS    
    public handleAdjustmentFormInputChange(n: any, v: any) {
        let changes = Object.assign({}, this.state.formChanges);
        changes[n] = Number(v);

        this.setState({ formChanges: changes });
    }

    public handleAdjustmentFormSubmit(e: any) {
        // console.log('handleAdjustmentFormSubmit', e);
    }

    public handleItemChange(event: any) {
        const itemField = event.field;
        const itemValueNew = event.value;
        const itemValueOld = event.dataItem[itemField];
        const itemId = event.dataItem.inventoryId;
        const origItem = _.find(this.originalExtendedInventoryCache, { 'inventoryId': itemId });
        const origValue = _.get(origItem, itemField);

        // console.log(`handleItemChange id ${itemId} field ${itemField} changed from ${itemValueOld} to ${itemValueNew}. orig ${origValue}`, origItem);

        let inv = _.cloneDeep(this.state.inventoryChanges);
        if (!_.find(inv, { 'inventoryId': itemId })) {
            const newItem = { inventoryId: itemId, };
            inv.push(newItem);
        }

        let item = _.find(inv, { 'inventoryId': itemId });
        if (item && _.get(item, itemField) !== itemValueNew) {
            if (itemValueNew === origValue) {
                // New value same as original, delete any change
                _.unset(item, itemField);
            } else {
                // New value differs from original
                _.set(item, itemField, itemValueNew);
            }

            if (_.keys(_.omit(item, 'inventoryId')).length === 0) {
                // No more changes for this item, remove it
                inv = _.reject(inv, { inventoryId: itemId });
            }

            _.set(event.dataItem, itemField, itemValueNew);

            this.setState({ inventoryChanges: inv });
        }
    }

    public getIsHypothetical() {
        const isHypothetical = (this.state.inventoryChanges.length === 0 && _.keys(this.state.formChanges).length === 0) ? false : true;

        return isHypothetical;
    }

    // HANDLERS
    public handleInviteButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.inviteAdd(associationId)); // this.props.match.params.associationId));
    }

    public handleToggleInvite(portalInvite: any) {
        this.setState({ showModalToggleInvite: true, toggleInvite: _.cloneDeep(portalInvite) });
    }

    public handleShowModalChangeInvite() {
        // console.log(`handleShowModalChangeInvite`);
        this.setState({ showModalToggleInvite: true });
    }

    public handleCancelAndCloseModalChangeInvite() {
        // console.log(`handleCancelAndCloseModalChangeInvite`);
        this.setState({ showModalToggleInvite: false, toggleInvite: {} });
    }

    public handleConfirmModalChangeInvite(portalInvite: any, action: any) {
        if (!portalInvite || !_.includes([PortalAccessActions.Enable, PortalAccessActions.Disable], action)) { return; }

        // console.log(`handleConfirmModalChangeInvite action ${action} on ${_.get(portalInvite, '_id', '')}`);

        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = {
            toggleClientInvite: portalInvite,
            action: action,
        };

        this.props.toggleClientInvite(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                // this.originalFormDataCache = {};

                // this.setState({ formChanges: {}, showModalCreateClientInvite: false });

                this.setState({ showModalToggleInvite: false, toggleInvite: {} });
            } else {
                // Error during creation...
            }
        });
    }

    // RENDERERS    
    public renderHeaderButtonToolbar = (assocId: any) => {
        let buttonProps = {
            bsSize: 'sm',
            bsStyle: 'info',
        };

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={2}>
                            <ButtonToolbar>
                                <Button {...buttonProps} onClick={e => { this.handleInviteButton(e); }}>
                                    <i className="rs-icon rs-icon-user-plus" /> {this.appText('portal-access.invite-to-portal')}
                                </Button>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderConfirmChangePortalInvite() {
        const toggleInvite = _.get(this.state, 'toggleInvite');
        const user = _.get(toggleInvite, 'user');
        const association = _.get(toggleInvite, 'association');

        let inviteAction = PortalAccessActions.Disable;
        let actionName = this.appText('modals.toggle-invite.disable');

        let modalBody = (<div />);
        if (toggleInvite && user && association) {
            // determine if we're enabling/deactivating portal access
            const isValid = (UserUtilities.IsPortalInviteValid(toggleInvite)) ? true : false;

            if (!isValid) {
                inviteAction = PortalAccessActions.Enable;
                actionName = this.appText('modals.toggle-invite.allow');
            }

            const defaultInputProps = { colLabelXs: 3, colInputXs: 9 };

            let inputPropsLargeTextDebounced = _.merge({}, defaultInputProps, {
                handleChange: (n, v) => { return; },
                className: 'inventory-detail-input-full-width',
            });

            const expireDate = (inviteAction === PortalAccessActions.Enable) ? moment().add('months', 6).toDate() : _.get(toggleInvite, 'roleExpirationDate');
            const expiresOn = (expireDate) ? moment(expireDate).format(AppConfiguration.getMomentShortDateFormat()) : '';

            const expireDateValue = (expiresOn) ? expiresOn : this.appText('modals.toggle-invite.deactivate-never');

            modalBody = (
                <div>
                    <span>{this.appText('modals.toggle-invite.are-you-sure')} <strong>{actionName}</strong> </span>
                    <span>{this.appText('modals.toggle-invite.portal-access-for')}:</span>
                    <div className="modal-portal-access-toggle-user">
                        <KendoWrappers.KendoDisabledInputHidden
                            name="_modal.user"
                            label={this.appText('modals.toggle-invite.user')}
                            value={Formatters.composeUsernameLabel(user, true)}
                            {...inputPropsLargeTextDebounced}
                        />

                        <KendoWrappers.KendoDisabledInputHidden
                            name="_modal.association"
                            label={this.appText('modals.toggle-invite.association')}
                            value={Formatters.composeAssociationNameLabel(association, true)}
                            {...inputPropsLargeTextDebounced}
                        />

                        <KendoWrappers.KendoDisabledInputHidden
                            name="_modal.role"
                            label={this.appText('modals.toggle-invite.access-level')}
                            value={UserUtilities.GetRoleText(Formatters.getStr(toggleInvite, 'roleName'))}
                            {...inputPropsLargeTextDebounced}
                        />

                        <KendoWrappers.KendoDisabledInputHidden
                            name="_modal.expire"
                            label={this.appText('modals.toggle-invite.deactivate-on')}
                            value={expireDateValue}
                            {...inputPropsLargeTextDebounced}
                        />
                    </div>
                </div>
            );
        }

        return (
            <Modal show={this.state.showModalToggleInvite} onHide={() => this.handleCancelAndCloseModalChangeInvite()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{actionName} {this.appText('modals.toggle-invite.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalBody}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={(e) => { this.handleConfirmModalChangeInvite(toggleInvite, inviteAction); }}>{this.appText('modals.toggle-invite.confirm')}</Button>
                    <Button onClick={(e) => { this.handleCancelAndCloseModalChangeInvite(); }}>{this.appText('app.common.buttons.cancel', false)}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public render() {
        const { currentOrder, currentOrderChart, portalInvites, associationId } = this.props;
        const association = this.extendAssociationData(this.props.association);
        const inventory: Array<any> = this.extendInventoryData(this.props.inventory, this.state.inventoryChanges, association);

        let cloneCurrentOrderChart = _.cloneDeep(currentOrderChart);
        let cloneCurrentOrder = _.cloneDeep(currentOrder);

        if (!currentOrder || !currentOrderChart || !association || !inventory) { return (<div />); }

        const isHypothetical = this.getIsHypothetical();
        const formData = this.getFormData(cloneCurrentOrderChart, cloneCurrentOrder);

        cloneCurrentOrderChart.begBalance = formData.beginningBalance;
        cloneCurrentOrderChart.interest = formData.interest;
        cloneCurrentOrderChart.inflation = formData.inflation;
        cloneCurrentOrderChart.recommendedMRCVal = formData.mrcValue;
        cloneCurrentOrderChart.years = formData.years;
        // cloneCurrentOrderChart.currentYears = formData.years;
        cloneCurrentOrderChart.recommendedAnnualIncrease = formData.annualIncrease;
        cloneCurrentOrderChart.recommendedSecondaryAnnualIncrease = formData.secondaryAnnualIncrease;
        cloneCurrentOrderChart.sa = formData.specialAssessment;

        const inventoryIncluded = _.cloneDeep(_.filter(inventory, e => _.get(e, 'included', false)));

        const calculationResult = ReportLogic.computeFundingCalculations(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears);

        const calculations = AppUtilities.getArray(calculationResult, 'recordSet');
        const calculationsCorrected = _.take(calculations, ReportCommon.CalculateYears); // formData.calculateYears);
        const invMap = AppUtilities.getArray(calculationsCorrected, 'invMap');

        const percentFunded = ReportLogic.getPercentFunded(calculationsCorrected, isHypothetical);

        // auth
        const auth = new Auth();
        const isStaff = (auth.isAdmin() || auth.isEmployee()) ? true : false;
        const isClient = auth.isClient();
        const userObjId = auth.getSessionUserObjectId();

        let portalAccessSection = (<div />);
        let portalAccessSectionVisible = (isStaff) ? true : false;

        // console.log(`report/index.tsx render`, { formData, cloneCurrentOrderChart, cloneCurrentOrder, calculationsCorrected });

        // console.log(`admin ${auth.isAdmin()} employee ${auth.isEmployee()} isStaff ${isStaff} isClient ${isClient} canViewPortalInvites ${canViewPortalInvites} _.size(portalInvites) ${_.size(portalInvites)}`);

        if (!isStaff && isClient && _.size(portalInvites)) {
            // Does current user have client roles and PortalAdmin role for this assoc?
            let isClientPortalAdmin = false;
            let activePortalInvites: any = UserUtilities.GetValidPortalInvites(portalInvites);
            if (_.size(activePortalInvites)) {
                const filterAdminUser = UserUtilities.FilterPortalInvites(activePortalInvites, PortalRoles.PortalAdmin.RoleName);
                isClientPortalAdmin = (_.find(filterAdminUser, (o) => _.get(o, 'user.userObjectId', '') === userObjId)) ? true : false;
                // console.log(`isClientPortalAdmin ${isClientPortalAdmin} filterAdminUser:`, filterAdminUser);
            }
            portalAccessSectionVisible = (isClient && isClientPortalAdmin) ? true : false;
            // console.log(`isStaff ${isStaff} isClient ${isClient} isClientPortalAdmin ${isClientPortalAdmin} canViewPortalInvites ${canViewPortalInvites} activePortalInvites:`, activePortalInvites);
        }
        // else {
        //     console.log(`isStaff ${isStaff} isClient ${isClient} canViewPortalInvites ${canViewPortalInvites}`);
        // }

        if (portalAccessSectionVisible) {
            const buildPortalManagers = (pi: any) => {
                const validPortalInvites: any = UserUtilities.GetValidPortalInvites(pi);

                const filterAdminUser = UserUtilities.FilterPortalInvites(validPortalInvites, PortalRoles.PortalAdmin.RoleName);
                let usersAdmin = _.map(filterAdminUser, (o) => {
                    const canToggle = (isStaff || _.get(o, 'user.userObjectId', '') === userObjId) ? true : false;
                    return UserUtilities.BuildInviteButtonAction(o, false, !canToggle, (e) => { this.handleToggleInvite(e); });
                });

                if (!_.size(usersAdmin)) {
                    usersAdmin = [UserUtilities.BuildEmptyPortalInvites(associationId, PortalRoles.PortalAdmin.RoleName, this.appText('portal-access.none-assigned'))];
                }

                return usersAdmin;
            };

            const buildPortalAllowedUsers = (pi: any) => {
                const validPortalInvites: any = UserUtilities.GetValidPortalInvites(pi);

                const filterAllowedUser = UserUtilities.FilterPortalInvites(validPortalInvites, PortalRoles.PortalUser.RoleName);
                let usersAllowed = _.map(filterAllowedUser, (o) => {
                    return UserUtilities.BuildInviteButtonAction(o, false, false, (e) => { this.handleToggleInvite(e); });
                });

                if (!_.size(usersAllowed)) {
                    usersAllowed = [UserUtilities.BuildEmptyPortalInvites(associationId, PortalRoles.PortalUser.RoleName, this.appText('portal-access.none-assigned'))];
                }

                return usersAllowed;
            };

            const buildPortalDeactivatedUsers = (pi: any) => {
                const invalidPortalInvites: any = UserUtilities.GetInvalidPortalInvites(pi);

                let usersDeactive = _.map(invalidPortalInvites, (o) => {
                    let res = (
                        <div key={`div-portalinvite-${_.get(o, 'association.associationObjectId', '')}-${_.get(o, 'user.userObjectId', '')}`}>
                            {UserUtilities.BuildInviteButtonAction(o, true, false, (e) => { this.handleToggleInvite(e); })}
                        </div>
                    );
                    return res;
                });

                if (!_.size(usersDeactive)) {
                    usersDeactive = [UserUtilities.BuildEmptyPortalInvites(associationId, 'deactivated', this.appText('portal-access.none-deactivated'))];
                }

                return usersDeactive;
            };

            const headerButtons = this.renderHeaderButtonToolbar(associationId);

            portalAccessSection = (
                <div>
                    {headerButtons}

                    <WhiteBoxPageSection pageSectionColXl={12} pageSectionColMd={12} title={this.appText('manage-user-access')}>
                        <Row>
                            <Col xs={12} md={4}>
                                <Row className="entity-detail-section">
                                    <Col xs={12}>
                                        <h5><b>{this.appText('portal-access.managers')}&nbsp;</b></h5>
                                        {buildPortalManagers(portalInvites)}
                                    </Col>
                                </Row>
                            </Col>

                            <Col xs={12} md={4}>
                                <Row className="entity-detail-section">
                                    <Col xs={12}>
                                        <h5><b>{this.appText('portal-access.allowed-users')}&nbsp;</b></h5>
                                        {buildPortalAllowedUsers(portalInvites)}
                                    </Col>
                                </Row>
                            </Col>

                            <Col xs={12} md={4}>
                                <Row className="entity-detail-section">
                                    <Col xs={12}>
                                        <h5><b>{this.appText('portal-access.deactivated-users')}&nbsp;</b></h5>
                                        {buildPortalDeactivatedUsers(portalInvites)}
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </WhiteBoxPageSection>
                </div>
            );
        }

        const pageTitle = this.appTextPageTitleAppendString(Formatters.getStr(association, 'name'));
        const page = (
            <Row>
                <Col xs={12}>
                    {this.renderConfirmChangePortalInvite()}
                    {portalAccessSection}

                    <WhiteBoxPageSection title={this.appText('report-parameters')}>
                        <Row>
                            {/* Left column */}
                            <Col xs={12} xl={9}>
                                <ReportCharts.ChartTabs
                                    calculations={calculationsCorrected}
                                    inventoryMap={invMap}
                                    tabsId="chartTabs"
                                    appText={(e) => this.appText(e)}
                                    isHypothetical={isHypothetical}
                                    beginYear={_.get(cloneCurrentOrderChart, 'beginYear', new Date().getFullYear())}
                                    yearsCount={formData.calculateYears}
                                    appTextGridColumnHeader={(e) => this.appTextGridColumnHeader(e)}
                                    scenarioLabel={' '}
                                />
                            </Col>

                            {/* Right column form */}
                            <Col xs={12} md={6} xl={3}>
                                <ReportChartForm.ChartForm
                                    appText={(e) => this.appText(e)}
                                    handleAdjustmentFormInputChange={(n, v) => this.handleAdjustmentFormInputChange(n, v)}
                                    handleSubmitForm={(e) => this.handleAdjustmentFormSubmit(e)}
                                    formData={formData}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12}>
                                <PercentFundedDisplay.PercentFundedSection
                                    appText={(e) => this.appText(e)}
                                    percentFunded={percentFunded}
                                    isHypothetical={isHypothetical}
                                    fundingAmount={undefined}
                                    fullyFunded={undefined}
                                />
                            </Col>
                        </Row>
                    </WhiteBoxPageSection>

                    <div className="report-inventory-grids">
                        <ReportInventoryGrids.InventoryGridsSection
                            appTextGridColumnHeader={(e) => this.appTextGridColumnHeader(e)}
                            gridTitlePrefix={this.appText('grid-title-prefix')}
                            sectionTitle={this.appText('inventory-analyzed')}
                            inventory={inventory}
                            inventorySubgroups={_.get(association, 'inventorySubgroups', [])}
                            onInventoryItemChange={(e) => { this.handleItemChange(e); }}
                        />
                    </div>
                </Col>
            </Row>
        );

        const { ...rest } = this.props;
        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = props => ApiEndpoints.getReportForAssociationUrl(props.match.params.associationId);

const mapPropsToConfigs = (props) => ReduxQueries.Queries.getReportForAssociationRequest(
    getApiUrl(props),
    ReduxQueries.PropsAccessToken(props)
);

const mapStateToProps = (state, props) => {
    const associationId = _.get(props, 'match.params.associationId', 0);

    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(props), state, props, {
        associationId: associationId,
        association: state.entities.reportAssociation,
        currentOrder: state.entities.reportAssociationCurrentOrder,
        currentOrderChart: state.entities.reportAssociationCurrentOrderChart,
        inventory: state.entities.reportAssociationInventory,
        scenarios: state.entities.reportAssociationScenarios,
        portalInvites: state.entities.reportAssociationPortalInvites,
    });
};

const mapDispatchToProps = {
    toggleClientInvite: MutateActions.toggleClientInviteMutateAsync,
    navigateTo: (path) => push(path)
};

const AssociationReportContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(AssociationReport);

export default withRouter(AssociationReportContainer);



// WEBPACK FOOTER //
// ./src/report/index.tsx